import type { RequestListState } from "../requestListSchema";

interface Chip {
  key: string;
  label: string;
  clear: () => void;
}

interface Props {
  state: RequestListState;
  defaultDateRange: number;
  onClear: (patch: Partial<RequestListState>) => void;
  onClearAll: () => void;
}

// Maps each filter dim to the URL field plus a label prefix; keeps ActiveChips below tight.
// Note: raw `status` is intentionally NOT listed here — the UI no longer surfaces raw status
// chips. `calculatedStatus` IS listed because the filter rail now exposes it as "Status".
const FILTER_DIMS: Array<{ key: keyof RequestListState; label: string }> = [
  { key: "requestType", label: "Type" },
  { key: "calculatedStatus", label: "Status" },
  { key: "client", label: "Client" },
  { key: "lob", label: "LOB" },
  { key: "eng", label: "Eng" },
  { key: "division", label: "Division" },
  { key: "manager", label: "Manager" },
  { key: "team", label: "Team" },
  { key: "assigned", label: "Owner" },
  { key: "country", label: "Country" },
  { key: "bucket", label: "Slowness" },
];

export function ActiveChips({ state, defaultDateRange, onClear, onClearAll }: Props) {
  const chips: Chip[] = [];

  // Live search text (state.q) is NOT shown as a chip — it lives in the SearchInput. Once
  // the user presses Enter, the term moves to state.qPills and gets a chip here.
  for (const pill of state.qPills) {
    chips.push({
      key: `qPill:${pill}`,
      label: `"${pill}"`,
      clear: () => onClear({ qPills: state.qPills.filter((p) => p !== pill) }),
    });
  }

  if (state.from && state.to) {
    chips.push({
      key: "date",
      label: `${state.from} → ${state.to}`,
      clear: () => onClear({ from: undefined, to: undefined, dateRange: defaultDateRange }),
    });
  } else if (state.dateRange !== defaultDateRange) {
    chips.push({
      key: "date",
      label: `Last ${state.dateRange}d`,
      clear: () => onClear({ dateRange: defaultDateRange }),
    });
  }

  for (const dim of FILTER_DIMS) {
    const values = state[dim.key] as string[];
    for (const v of values) {
      chips.push({
        key: `${dim.key}:${v}`,
        label: v,
        clear: () =>
          onClear({
            [dim.key]: values.filter((x) => x !== v),
          } as Partial<RequestListState>),
      });
    }
  }

  if (state.rework) {
    chips.push({ key: "rework", label: "Has rework", clear: () => onClear({ rework: false }) });
  }
  if (state.onboardingsOnly) {
    chips.push({
      key: "onboardingsOnly",
      label: "Onboarding",
      clear: () => onClear({ onboardingsOnly: false }),
    });
  }
  if (state.redeployment) {
    chips.push({
      key: "redeployment",
      label: "Redeployment",
      clear: () => onClear({ redeployment: false }),
    });
  }
  if (state.quickFilter) {
    chips.push({
      key: "quickFilter",
      label: state.quickFilter === "slow" ? "Slow + critical" : "In progress",
      clear: () => onClear({ quickFilter: undefined }),
    });
  }

  if (chips.length === 0) return null;

  return (
    <div className="chip-row" style={{ padding: "8px 12px", borderBottom: "1px solid var(--border-subtle)" }}>
      <span
        style={{
          fontSize: 11,
          color: "var(--fg-2)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          fontWeight: 600,
        }}
      >
        Filters:
      </span>
      {chips.map((c) => (
        <span key={c.key} className="chip" onClick={c.clear}>
          {c.label} <span className="x">×</span>
        </span>
      ))}
      <span className="chip clear-all" onClick={onClearAll}>
        Clear all
      </span>
    </div>
  );
}
